import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";
import { useNavigate } from "react-router-dom";

const Services = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedServices, setExpandedServices] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive"

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showSubServiceModal, setShowSubServiceModal] = useState(false);
  const [showCityPricingModal, setShowCityPricingModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingSubService, setEditingSubService] = useState(null);
  const [editingCityPricing, setEditingCityPricing] = useState(null);
  const [selectedSubServiceForPricing, setSelectedSubServiceForPricing] =
    useState(null);

  // Form states
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    image: null,
  });
  const [subServiceForm, setSubServiceForm] = useState({
    name: "",
    description: "",
    serviceId: "",
    image: null,
  });
  const [cityPricingForm, setCityPricingForm] = useState({
    city: "",
    state: "",
    zone: "",
    price2hr: "",
    price4hr: "",
    price6hr: "",
    price8hr: "",
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    navigate("/login");
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchServices = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}admin/services`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setServices(data.data.services || []);
      } else {
        setError(data.message || "Failed to fetch services");
      }
    } catch (err) {
      setError("Error connecting to services registry");
      console.error("Services fetch error:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const toggleServiceExpansion = (serviceId) => {
    setExpandedServices((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  const toggleAllExpanded = (expand) => {
    const next = {};
    if (expand) {
      services.forEach((s) => {
        next[s.id] = true;
      });
    }
    setExpandedServices(next);
  };

  // Real Database Overview Stats
  const stats = useMemo(() => {
    const totalServices = services.length;
    const activeServices = services.filter((s) => s.isActive).length;
    const totalSubServices = services.reduce(
      (acc, s) => acc + (s.subServices?.length || 0),
      0,
    );
    const totalBookings = services.reduce(
      (acc, s) => acc + (s.stats?.totalBookings || 0),
      0,
    );
    return {
      totalServices,
      activeServices,
      totalSubServices,
      totalBookings,
    };
  }, [services]);

  // Filtered Services List
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Status filter
      if (statusFilter === "active" && !service.isActive) return false;
      if (statusFilter === "inactive" && service.isActive) return false;

      // Search query
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const matchServiceName = service.name?.toLowerCase().includes(q);
      const matchServiceDesc = service.description?.toLowerCase().includes(q);
      const matchSubService = service.subServices?.some(
        (sub) =>
          sub.name?.toLowerCase().includes(q) ||
          sub.description?.toLowerCase().includes(q),
      );

      return matchServiceName || matchServiceDesc || matchSubService;
    });
  }, [services, statusFilter, searchQuery]);

  // Service CRUD Functions
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name", serviceForm.name);
    formData.append("description", serviceForm.description);
    if (serviceForm.image) {
      formData.append("image", serviceForm.image);
    }

    try {
      const url = editingService
        ? `${BASE_URL}admin/services/${editingService.id}`
        : `${BASE_URL}admin/services`;

      const response = await fetch(url, {
        method: editingService ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.status === "success") {
        setSuccess(
          `Service ${editingService ? "updated" : "created"} successfully!`,
        );
        setShowServiceModal(false);
        resetServiceForm();
        fetchServices();
      } else {
        setError(data.message || "Failed to save service");
      }
    } catch (err) {
      setError("Error saving service");
      console.error("Service save error:", err);
    }
    setLoading(false);
  };

  const handleSubServiceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name", subServiceForm.name);
    formData.append("description", subServiceForm.description);
    formData.append("serviceId", subServiceForm.serviceId);
    if (subServiceForm.image) {
      formData.append("image", subServiceForm.image);
    }

    try {
      const url = editingSubService
        ? `${BASE_URL}admin/sub-services/${editingSubService.id}`
        : `${BASE_URL}admin/sub-services`;

      const response = await fetch(url, {
        method: editingSubService ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.status === "success") {
        setSuccess(
          `Sub-service ${
            editingSubService ? "updated" : "created"
          } successfully!`,
        );
        setShowSubServiceModal(false);
        resetSubServiceForm();
        fetchServices();
      } else {
        setError(data.message || "Failed to save sub-service");
      }
    } catch (err) {
      setError("Error saving sub-service");
      console.error("Sub-service save error:", err);
    }
    setLoading(false);
  };

  const handleCityPricingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!selectedSubServiceForPricing || !selectedSubServiceForPricing.id) {
      setError("No sub-service selected for pricing");
      setLoading(false);
      return;
    }

    try {
      const url = `${BASE_URL}admin/sub-services/${selectedSubServiceForPricing.id}/city-pricing`;
      const method = editingCityPricing ? "PUT" : "POST";

      const requestBody = {
        type: "city",
        city: cityPricingForm.city,
        state: cityPricingForm.state,
        zone: cityPricingForm.zone,
        price2hr: parseFloat(cityPricingForm.price2hr),
        price4hr: parseFloat(cityPricingForm.price4hr),
        price6hr: parseFloat(cityPricingForm.price6hr),
        price8hr: parseFloat(cityPricingForm.price8hr),
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === "success") {
        setSuccess(
          `City pricing ${
            editingCityPricing ? "updated" : "added"
          } successfully!`,
        );
        setShowCityPricingModal(false);
        resetCityPricingForm();
        fetchServices();
      } else {
        setError(
          data.message ||
            `Failed to ${editingCityPricing ? "update" : "add"} city pricing`,
        );
      }
    } catch (err) {
      console.log(err);
      setError(
        `Error ${editingCityPricing ? "updating" : "adding"} city pricing`,
      );
      console.error("City pricing error:", err);
    }
    setLoading(false);
  };

  // Reset form functions
  const resetServiceForm = () => {
    setServiceForm({ name: "", description: "", image: null });
    setEditingService(null);
  };

  const resetSubServiceForm = () => {
    setSubServiceForm({
      name: "",
      description: "",
      serviceId: "",
      image: null,
    });
    setEditingSubService(null);
  };

  const resetCityPricingForm = () => {
    setCityPricingForm({
      city: "",
      state: "",
      zone: "",
      price2hr: "",
      price4hr: "",
      price6hr: "",
      price8hr: "",
    });
    setSelectedSubServiceForPricing(null);
    setEditingCityPricing(null);
  };

  // Modal handlers
  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description,
        image: null,
      });
    } else {
      resetServiceForm();
    }
    setShowServiceModal(true);
  };

  const openSubServiceModal = (subService = null, serviceId = null) => {
    if (subService) {
      setEditingSubService(subService);
      setSubServiceForm({
        name: subService.name,
        description: subService.description,
        serviceId: subService.serviceId,
        image: null,
      });
    } else {
      resetSubServiceForm();
      setSubServiceForm((prev) => ({ ...prev, serviceId: serviceId || "" }));
    }
    setShowSubServiceModal(true);
  };

  const openCityPricingModal = (subService, cityPricing = null) => {
    setSelectedSubServiceForPricing(subService);

    if (cityPricing) {
      setEditingCityPricing(cityPricing);
      setCityPricingForm({
        city: cityPricing.city,
        state: cityPricing.state,
        zone: cityPricing.zone,
        price2hr: cityPricing.hourlyPricing
          ? cityPricing.hourlyPricing["2hr"]?.toString() || ""
          : "",
        price4hr: cityPricing.hourlyPricing
          ? cityPricing.hourlyPricing["4hr"]?.toString() || ""
          : "",
        price6hr: cityPricing.hourlyPricing
          ? cityPricing.hourlyPricing["6hr"]?.toString() || ""
          : "",
        price8hr: cityPricing.hourlyPricing
          ? cityPricing.hourlyPricing["8hr"]?.toString() || ""
          : "",
      });
    } else {
      resetCityPricingForm();
      setSelectedSubServiceForPricing(subService);
    }

    setShowCityPricingModal(true);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content Area */}
      <main className="flex-1 lg:ml-72 min-h-screen flex flex-col">
        {/* Sticky Executive Topbar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200/80 px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-400">
                <span>Admin</span>
                <span>/</span>
                <span className="text-stone-700">Services Management</span>
              </div>
              <h1 className="text-xl font-extrabold text-stone-900 tracking-tight flex items-center gap-2">
                <span>Trade & Service Categories</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#F8D346]/20 text-stone-900 border border-[#F8D346]/40">
                  {stats.totalServices} Parent Services
                </span>
              </h1>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={() => openServiceModal()}
                className="px-4 py-2 bg-stone-900 hover:bg-stone-800 text-[#F8D346] text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5"
              >
                <svg
                  className="w-4 h-4 text-[#F8D346]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>Add New Service</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Body Container */}
        <div className="p-4 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
          {/* Notifications */}
          {success && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-emerald-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-rose-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Section 1: 4-Grid Key Intelligence Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Total Services
                </span>
                <div className="w-8 h-8 rounded-lg bg-stone-100 text-stone-800 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {stats.totalServices}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Parent trade categories
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Active Services
                </span>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-emerald-600">
                {stats.activeServices}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Live for consumer booking
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Sub-Services
                </span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {stats.totalSubServices}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Granular trade skills
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm relative overflow-hidden group hover:border-[#F8D346] transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                  Total Bookings
                </span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-black text-stone-900">
                {stats.totalBookings}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Jobs dispatched to date
              </div>
            </div>
          </div>

          {/* Section 2: Search, Filters & Bulk Actions */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search services or sub-services by name or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-50 hover:bg-stone-100/60 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#F8D346] transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs font-bold"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Status Filter Tabs & Expand Toggle */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 p-1 bg-stone-100 rounded-xl">
                  <button
                    onClick={() => setStatusFilter("all")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      statusFilter === "all"
                        ? "bg-white text-stone-900 shadow-sm"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    All ({stats.totalServices})
                  </button>
                  <button
                    onClick={() => setStatusFilter("active")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      statusFilter === "active"
                        ? "bg-white text-stone-900 shadow-sm"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    Active ({stats.activeServices})
                  </button>
                  <button
                    onClick={() => setStatusFilter("inactive")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      statusFilter === "inactive"
                        ? "bg-white text-stone-900 shadow-sm"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    Inactive ({stats.totalServices - stats.activeServices})
                  </button>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => toggleAllExpanded(true)}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-colors"
                    title="Expand all services"
                  >
                    Expand All
                  </button>
                  <button
                    onClick={() => toggleAllExpanded(false)}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-colors"
                    title="Collapse all services"
                  >
                    Collapse All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Services & SubServices List */}
          {loading ? (
            <div className="p-16 text-center bg-white rounded-2xl border border-stone-200/80 shadow-sm">
              <div className="w-10 h-10 border-4 border-[#F8D346] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-sm font-bold text-stone-700">
                Loading service categories...
              </p>
              <p className="text-xs text-stone-400 mt-1">
                Fetching trades & pricing models
              </p>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="p-16 text-center bg-white rounded-2xl border border-stone-200/80 shadow-sm">
              <div className="w-16 h-16 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h4 className="text-base font-bold text-stone-800 mb-1">
                No Services Found
              </h4>
              <p className="text-xs text-stone-400 mb-4">
                {searchQuery
                  ? `No service matches "${searchQuery}"`
                  : "No services are currently configured in the database."}
              </p>
              <button
                onClick={() => openServiceModal()}
                className="px-4 py-2 bg-stone-900 hover:bg-stone-800 text-[#F8D346] rounded-xl text-xs font-bold transition-all shadow-sm"
              >
                Create First Service
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredServices.map((service) => {
                const isExpanded = expandedServices[service.id];
                return (
                  <div
                    key={service.id}
                    className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden transition-all group"
                  >
                    {/* Parent Service Header */}
                    <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Service Thumbnail */}
                        <div className="w-14 h-14 rounded-xl overflow-hidden border border-stone-200 bg-stone-100 shrink-0 shadow-xs">
                          {service.imageUrl ? (
                            <img
                              src={service.imageUrl}
                              alt={service.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-400 bg-stone-50">
                              <svg
                                className="w-7 h-7"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Title & Description */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h2 className="text-lg font-black text-stone-900 tracking-tight">
                              {service.name}
                            </h2>
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                service.isActive
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-stone-100 text-stone-600 border border-stone-200"
                              }`}
                            >
                              {service.isActive ? "Active" : "Inactive"}
                            </span>
                            <span className="text-xs text-stone-400 font-mono">
                              ID: {service.id?.substring(0, 8)}...
                            </span>
                          </div>

                          <p className="text-xs text-stone-600 line-clamp-2 mb-2 leading-relaxed">
                            {service.description}
                          </p>

                          <div className="flex flex-wrap items-center gap-3 text-[11px] text-stone-400">
                            {service.createdBy && (
                              <span>By: {service.createdBy}</span>
                            )}
                            <span>•</span>
                            <span>
                              Created:{" "}
                              {new Date(service.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Stats & Actions */}
                      <div className="flex flex-wrap items-center gap-4 self-end lg:self-center">
                        <div className="flex items-center gap-2">
                          <div className="text-center px-3 py-1.5 bg-stone-50 rounded-xl border border-stone-200/60">
                            <div className="text-base font-black text-stone-900">
                              {service.stats?.totalSubServices ||
                                service.subServices?.length ||
                                0}
                            </div>
                            <div className="text-[10px] font-bold text-stone-500 uppercase">
                              Sub Services
                            </div>
                          </div>

                          <div className="text-center px-3 py-1.5 bg-stone-50 rounded-xl border border-stone-200/60">
                            <div className="text-base font-black text-emerald-700">
                              {service.stats?.totalBookings || 0}
                            </div>
                            <div className="text-[10px] font-bold text-stone-500 uppercase">
                              Bookings
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openServiceModal(service)}
                            className="p-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors shadow-xs"
                            title="Edit Service"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>

                          <button
                            onClick={() =>
                              openSubServiceModal(null, service.id)
                            }
                            className="px-3 py-2 rounded-xl bg-stone-900 hover:bg-stone-800 text-[#F8D346] text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                            title="Add Sub-Service to this category"
                          >
                            <svg
                              className="w-3.5 h-3.5 text-[#F8D346]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                            <span>Add Sub-Service</span>
                          </button>

                          <button
                            onClick={() => toggleServiceExpansion(service.id)}
                            className="p-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors shadow-xs flex items-center gap-1 text-xs font-bold"
                            title="Toggle Sub-Services"
                          >
                            <span>
                              {isExpanded ? "Hide Details" : "View Skills"}
                            </span>
                            <svg
                              className={`w-4 h-4 transition-transform duration-200 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Sub Services Drawer */}
                    {isExpanded && (
                      <div className="p-5 sm:p-6 bg-stone-50 border-t border-stone-200/80 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-stone-900 text-sm flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#F8D346]"></span>
                            Configured Sub-Services ({service.subServices?.length || 0})
                          </h3>
                          <button
                            onClick={() =>
                              openSubServiceModal(null, service.id)
                            }
                            className="text-xs font-bold text-stone-700 hover:text-stone-900 flex items-center gap-1"
                          >
                            <span>+ Add Sub-Service</span>
                          </button>
                        </div>

                        {service.subServices &&
                        service.subServices.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {service.subServices.map((subService) => (
                              <div
                                key={subService.id}
                                className="bg-white rounded-2xl border border-stone-200/80 p-4 shadow-sm hover:border-stone-400 transition-all flex flex-col justify-between"
                              >
                                <div>
                                  {/* SubService Header */}
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className="w-12 h-12 rounded-xl overflow-hidden border border-stone-200 bg-stone-100 shrink-0">
                                      {subService.imageUrl ? (
                                        <img
                                          src={subService.imageUrl}
                                          alt={subService.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-stone-400 bg-stone-50 text-xs font-bold">
                                          SKILL
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-1 mb-1">
                                        <h4 className="font-bold text-sm text-stone-900 truncate">
                                          {subService.name}
                                        </h4>
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                            subService.isActive
                                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                              : "bg-stone-100 text-stone-600"
                                          }`}
                                        >
                                          {subService.isActive
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                      </div>
                                      <p className="text-xs text-stone-500 line-clamp-2">
                                        {subService.description}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Metadata Chips */}
                                  <div className="grid grid-cols-2 gap-2 text-xs py-2 border-y border-stone-100 my-3">
                                    <div>
                                      <span className="text-[10px] font-bold text-stone-400 uppercase block">
                                        Price Model
                                      </span>
                                      <span className="font-semibold text-stone-700 capitalize">
                                        {subService.priceType || "Standard"}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] font-bold text-stone-400 uppercase block">
                                        Universal
                                      </span>
                                      <span className="font-semibold text-stone-700">
                                        {subService.isUniversal ? "Yes" : "No"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Stats Pill */}
                                  <div className="flex items-center justify-between text-[11px] text-stone-500 mb-3 bg-stone-50 p-2 rounded-xl border border-stone-100">
                                    <span>
                                      👥 {subService.stats?.totalProviders || 0}{" "}
                                      Providers
                                    </span>
                                    <span>
                                      📦 {subService.stats?.totalBookings || 0}{" "}
                                      Jobs
                                    </span>
                                    <span>
                                      🏙️{" "}
                                      {subService.stats?.totalCityPricing || 0}{" "}
                                      Cities
                                    </span>
                                  </div>
                                </div>

                                {/* Actions & City Pricing */}
                                <div className="space-y-3 pt-2">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() =>
                                        openSubServiceModal(subService)
                                      }
                                      className="flex-1 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-colors text-center"
                                    >
                                      Edit Details
                                    </button>
                                    <button
                                      onClick={() =>
                                        openCityPricingModal(subService)
                                      }
                                      className="flex-1 py-1.5 bg-stone-900 hover:bg-stone-800 text-[#F8D346] rounded-xl text-xs font-bold transition-colors text-center flex items-center justify-center gap-1"
                                    >
                                      <span>+ Add City Rate</span>
                                    </button>
                                  </div>

                                  {/* City Pricing Matrix Tags */}
                                  {subService.cityPricing &&
                                    subService.cityPricing.length > 0 && (
                                      <div className="pt-3 border-t border-stone-100">
                                        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-400 mb-2 flex items-center justify-between">
                                          <span>
                                            Regional Rates (
                                            {subService.cityPricing.length})
                                          </span>
                                        </div>
                                        <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                                          {subService.cityPricing.map(
                                            (pricing) => (
                                              <div
                                                key={pricing.id}
                                                className="bg-stone-50 rounded-xl p-2.5 border border-stone-200/70"
                                              >
                                                <div className="flex items-center justify-between mb-1.5">
                                                  <span className="text-xs font-bold text-stone-900">
                                                    {pricing.city},{" "}
                                                    {pricing.state}
                                                    {pricing.zone
                                                      ? ` (${pricing.zone})`
                                                      : ""}
                                                  </span>
                                                  <button
                                                    onClick={() =>
                                                      openCityPricingModal(
                                                        subService,
                                                        pricing,
                                                      )
                                                    }
                                                    className="text-[11px] font-bold text-amber-700 hover:text-amber-800 underline"
                                                  >
                                                    Edit Rate
                                                  </button>
                                                </div>
                                                {pricing.hourlyPricing && (
                                                  <div className="grid grid-cols-4 gap-1 text-[11px]">
                                                    <div className="bg-white p-1 rounded border border-stone-200 text-center">
                                                      <span className="text-stone-400 block text-[9px]">
                                                        2hr
                                                      </span>
                                                      <span className="font-bold text-emerald-700">
                                                        ₹
                                                        {pricing.hourlyPricing[
                                                          "2hr"
                                                        ] || 0}
                                                      </span>
                                                    </div>
                                                    <div className="bg-white p-1 rounded border border-stone-200 text-center">
                                                      <span className="text-stone-400 block text-[9px]">
                                                        4hr
                                                      </span>
                                                      <span className="font-bold text-emerald-700">
                                                        ₹
                                                        {pricing.hourlyPricing[
                                                          "4hr"
                                                        ] || 0}
                                                      </span>
                                                    </div>
                                                    <div className="bg-white p-1 rounded border border-stone-200 text-center">
                                                      <span className="text-stone-400 block text-[9px]">
                                                        6hr
                                                      </span>
                                                      <span className="font-bold text-emerald-700">
                                                        ₹
                                                        {pricing.hourlyPricing[
                                                          "6hr"
                                                        ] || 0}
                                                      </span>
                                                    </div>
                                                    <div className="bg-white p-1 rounded border border-stone-200 text-center">
                                                      <span className="text-stone-400 block text-[9px]">
                                                        8hr
                                                      </span>
                                                      <span className="font-bold text-emerald-700">
                                                        ₹
                                                        {pricing.hourlyPricing[
                                                          "8hr"
                                                        ] || 0}
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-white rounded-2xl border border-dashed border-stone-200">
                            <p className="text-xs font-semibold text-stone-400 mb-2">
                              No sub-services configured for this service yet.
                            </p>
                            <button
                              onClick={() =>
                                openSubServiceModal(null, service.id)
                              }
                              className="px-3 py-1.5 bg-stone-900 text-[#F8D346] text-xs font-bold rounded-xl shadow-sm"
                            >
                              Add First Sub-Service
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-stone-200">
            <div className="p-6">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-stone-100">
                <h2 className="text-lg font-black text-stone-900">
                  {editingService ? "Edit Service" : "Add Parent Service"}
                </h2>
                <button
                  onClick={() => setShowServiceModal(false)}
                  className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center text-sm font-bold transition-colors"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleServiceSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Service Name
                  </label>
                  <input
                    type="text"
                    required
                    value={serviceForm.name}
                    onChange={(e) =>
                      setServiceForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                    placeholder="e.g. Electrician, Plumbing, Deep Cleaning"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Description
                  </label>
                  <textarea
                    required
                    value={serviceForm.description}
                    onChange={(e) =>
                      setServiceForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346] resize-none"
                    placeholder="Brief description of the service scope"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Category Icon / Photo
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setServiceForm((prev) => ({
                        ...prev,
                        image: e.target.files[0],
                      }))
                    }
                    className="w-full text-xs text-stone-600 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200"
                  />
                  {editingService && (
                    <p className="text-[11px] text-stone-400 mt-1">
                      Leave empty to keep existing category photo
                    </p>
                  )}
                </div>

                <div className="flex gap-2.5 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowServiceModal(false)}
                    className="flex-1 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2.5 bg-stone-900 hover:bg-stone-800 text-[#F8D346] rounded-xl text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingService
                        ? "Update Service"
                        : "Create Service"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sub Service Modal */}
      {showSubServiceModal && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-stone-200">
            <div className="p-6">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-stone-100">
                <h2 className="text-lg font-black text-stone-900">
                  {editingSubService ? "Edit Sub-Service" : "Add Sub-Service Skill"}
                </h2>
                <button
                  onClick={() => setShowSubServiceModal(false)}
                  className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center text-sm font-bold transition-colors"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubServiceSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Parent Service
                  </label>
                  <select
                    required
                    value={subServiceForm.serviceId}
                    onChange={(e) =>
                      setSubServiceForm((prev) => ({
                        ...prev,
                        serviceId: e.target.value,
                      }))
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                  >
                    <option value="">Select a parent service</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Sub-Service Name
                  </label>
                  <input
                    type="text"
                    required
                    value={subServiceForm.name}
                    onChange={(e) =>
                      setSubServiceForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                    placeholder="e.g. Switchboard Repair, Tap Replacement"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Description
                  </label>
                  <textarea
                    required
                    value={subServiceForm.description}
                    onChange={(e) =>
                      setSubServiceForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346] resize-none"
                    placeholder="Description of the specialized trade task"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Skill Thumbnail
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setSubServiceForm((prev) => ({
                        ...prev,
                        image: e.target.files[0],
                      }))
                    }
                    className="w-full text-xs text-stone-600 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200"
                  />
                  {editingSubService && (
                    <p className="text-[11px] text-stone-400 mt-1">
                      Leave empty to preserve existing thumbnail
                    </p>
                  )}
                </div>

                <div className="flex gap-2.5 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowSubServiceModal(false)}
                    className="flex-1 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2.5 bg-stone-900 hover:bg-stone-800 text-[#F8D346] rounded-xl text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingSubService
                        ? "Update Sub-Service"
                        : "Create Sub-Service"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* City Pricing Modal */}
      {showCityPricingModal && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-stone-200">
            <div className="p-6">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-stone-100">
                <div>
                  <h2 className="text-lg font-black text-stone-900">
                    {editingCityPricing ? "Edit City Pricing" : "Add City Pricing"}
                  </h2>
                  {selectedSubServiceForPricing && (
                    <p className="text-xs text-stone-500 mt-0.5">
                      Target:{" "}
                      <span className="font-bold text-stone-800">
                        {selectedSubServiceForPricing.name}
                      </span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowCityPricingModal(false)}
                  className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center text-sm font-bold transition-colors"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCityPricingSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                      City
                    </label>
                    <input
                      type="text"
                      required
                      value={cityPricingForm.city}
                      onChange={(e) =>
                        setCityPricingForm((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      placeholder="e.g. Mumbai"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                      State
                    </label>
                    <input
                      type="text"
                      required
                      value={cityPricingForm.state}
                      onChange={(e) =>
                        setCityPricingForm((prev) => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                      className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      placeholder="e.g. Maharashtra"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Zone / Region
                  </label>
                  <input
                    type="text"
                    required
                    value={cityPricingForm.zone}
                    onChange={(e) =>
                      setCityPricingForm((prev) => ({
                        ...prev,
                        zone: e.target.value,
                      }))
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                    placeholder="e.g. Zone 1, Metro, North"
                  />
                </div>

                <div className="p-3 bg-stone-50 rounded-xl border border-stone-200/70 space-y-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                    Hourly Rates (INR ₹)
                  </span>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">
                        2 Hours (₹)
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={cityPricingForm.price2hr}
                        onChange={(e) =>
                          setCityPricingForm((prev) => ({
                            ...prev,
                            price2hr: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                        placeholder="Price 2hr"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">
                        4 Hours (₹)
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={cityPricingForm.price4hr}
                        onChange={(e) =>
                          setCityPricingForm((prev) => ({
                            ...prev,
                            price4hr: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                        placeholder="Price 4hr"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">
                        6 Hours (₹)
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={cityPricingForm.price6hr}
                        onChange={(e) =>
                          setCityPricingForm((prev) => ({
                            ...prev,
                            price6hr: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                        placeholder="Price 6hr"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">
                        8 Hours (₹)
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={cityPricingForm.price8hr}
                        onChange={(e) =>
                          setCityPricingForm((prev) => ({
                            ...prev,
                            price8hr: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                        placeholder="Price 8hr"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2.5 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowCityPricingModal(false)}
                    className="flex-1 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2.5 bg-stone-900 hover:bg-stone-800 text-[#F8D346] rounded-xl text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingCityPricing
                        ? "Update Pricing"
                        : "Save City Pricing"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
